import { ChangeDetectionStrategy, Component } from '@angular/core';
import { MngBaseButtonComponent } from './mng-base-button.component';
import { FlexDirective, LayoutAlignDirective, LayoutDirective, LayoutGapDirective } from '@ngbracket/ngx-layout/flex';
import { MngIconComponent } from '@mng/mng-icon';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatMenu, MatMenuContent, MatMenuTrigger } from '@angular/material/menu';
import { MngMenuButtonComponent } from './mng-menu-button.component';
import { NgTemplateOutlet } from '@angular/common';

@Component({
  selector: 'mng-bar-button',
  imports: [
    LayoutDirective,
    LayoutAlignDirective,
    LayoutGapDirective,
    FlexDirective,
    MngIconComponent,
    MatButton,
    MatTooltip,
    MatMenuTrigger,
    MatMenu,
    MatMenuContent,
    MngMenuButtonComponent,
    NgTemplateOutlet
  ],
  template: `
    <ng-template #tplButtonContent let-actionDef>
      <span style="pointer-events: none;" fxLayout="column" fxLayoutAlign="start center" fxLayoutGap="6px">
        @if (actionDef.iconTemplate) {
          <span [innerHTML]="actionDef.iconTemplate"></span>
        }
        <mng-icon [iconName]="iconName" [color]="iconColor" />
        <div fxFlex fxLayout="row" fxLayoutAlign="center center">
          <span [innerHTML]="sanitizeHtml(text)"></span>
          @if (actionDef.externalUrlTarget === '_blank') {
            <mng-icon style="margin-left: 4px" iconName="open-in-new" [inline]="true" [fontSize]="14" />
          }
          @if (actionDef.menu) {
            <mng-icon style="margin-left: 2px" iconName="menu-down" [inline]="true" [fontSize]="20" />
          }
        </div>
      </span>
    </ng-template>

    @if (isVisible) {
      <div
        [matTooltip]="isDisabled ? getDisabledTooltipMessage() : getTooltipMessage()"
        [matTooltipPosition]="tooltipPosition"
        [matTooltipShowDelay]="isDisabled ? 50 : tooltipDelay"
      >
        @if (!actionDef.menu && !actionDef.menuRef) {
          @if (actionDef.variant === 'basic') {
            <button
              mat-button
              class="bar-button"
              [color]="buttonColor"
              [style.width]="sanitizeStyle(actionDef.width)"
              [id]="getButtonId()"
              [disabled]="isDisabled"
              (click)="executeAction($event)"
            >
              <ng-container *ngTemplateOutlet="tplButtonContent; context: { $implicit: actionDef }" />
            </button>
          }
          @if (actionDef.variant === 'raised') {
            <button
              mat-raised-button
              class="bar-button"
              [color]="buttonColor"
              [style.width]="sanitizeStyle(actionDef.width)"
              [id]="getButtonId()"
              [disabled]="isDisabled"
              (click)="executeAction($event)"
            >
              <ng-container *ngTemplateOutlet="tplButtonContent; context: { $implicit: actionDef }" />
            </button>
          }
          @if (actionDef.variant === 'flat') {
            <button
              mat-flat-button
              class="bar-button"
              [color]="buttonColor"
              [style.width]="sanitizeStyle(actionDef.width)"
              [id]="getButtonId()"
              [disabled]="isDisabled"
              (click)="executeAction($event)"
            >
              <ng-container *ngTemplateOutlet="tplButtonContent; context: { $implicit: actionDef }" />
            </button>
          }
          @if (actionDef.variant === 'stroked') {
            <button
              mat-stroked-button
              class="bar-button"
              [color]="buttonColor"
              [style.width]="sanitizeStyle(actionDef.width)"
              [id]="getButtonId()"
              [disabled]="isDisabled"
              (click)="executeAction($event)"
            >
              <ng-container *ngTemplateOutlet="tplButtonContent; context: { $implicit: actionDef }" />
            </button>
          }
        } @else {
          @if (actionDef.variant === 'basic') {
            <button
              mat-button
              class="bar-button"
              [color]="buttonColor"
              [id]="getButtonId()"
              [disabled]="isDisabled"
              [matMenuTriggerFor]="actionDef.menu ? buttonMenu : actionDef.menuRef"
              [matMenuTriggerData]="{ actionArg: actionArg }"
              (click)="executeAction($event)"
            >
              <ng-container *ngTemplateOutlet="tplButtonContent; context: { $implicit: actionDef }" />
            </button>
          }
          @if (actionDef.variant === 'raised') {
            <button
              mat-raised-button
              class="bar-button"
              [color]="buttonColor"
              [id]="getButtonId()"
              [disabled]="isDisabled"
              [matMenuTriggerFor]="actionDef.menu ? buttonMenu : actionDef.menuRef"
              [matMenuTriggerData]="{ actionArg: actionArg }"
              (click)="executeAction($event)"
            >
              <ng-container *ngTemplateOutlet="tplButtonContent; context: { $implicit: actionDef }" />
            </button>
          }
          @if (actionDef.variant === 'flat') {
            <button
              mat-flat-button
              class="bar-button"
              [color]="buttonColor"
              [id]="getButtonId()"
              [disabled]="isDisabled"
              [matMenuTriggerFor]="actionDef.menu ? buttonMenu : actionDef.menuRef"
              [matMenuTriggerData]="{ actionArg: actionArg }"
              (click)="executeAction($event)"
            >
              <ng-container *ngTemplateOutlet="tplButtonContent; context: { $implicit: actionDef }" />
            </button>
          }
          @if (actionDef.variant === 'stroked') {
            <button
              mat-stroked-button
              class="bar-button"
              [color]="buttonColor"
              [id]="getButtonId()"
              [disabled]="isDisabled"
              [matMenuTriggerFor]="actionDef.menu ? buttonMenu : actionDef.menuRef"
              [matMenuTriggerData]="{ actionArg: actionArg }"
              (click)="executeAction($event)"
            >
              <ng-container *ngTemplateOutlet="tplButtonContent; context: { $implicit: actionDef }" />
            </button>
          }
          <mat-menu #buttonMenu="matMenu">
            <ng-template matMenuContent>
              @for (menuActionDef of actionDef.menu; track menuActionDef; let i = $index) {
                <mng-menu-button
                  [actionDef]="menuActionDef"
                  [actionArg]="actionArg"
                  (executeMenuAction)="executeMenuAction($event, i)"
                />
              }
            </ng-template>
          </mat-menu>
        }
      </div>
    }
  `,
  styles: [
    `
      :host {
        display: inline-block;
      }

      ::ng-deep .bar-button.mdc-button {
        height: 64px;
        line-height: 16px;
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class MngBarButtonComponent extends MngBaseButtonComponent {}
